<script setup lang="ts">
import type { AiCompanionTrainingReviewModel } from "@simbelapp/order-sdk";
import CopilotBlocks from "~/components/Copilot/conversation/CopilotBlocks.vue";
import CopilotFeedback from "~/components/Copilot/conversation/CopilotFeedback.vue";
import CopilotLoader from "~/components/Copilot/conversation/CopilotLoader.vue";
import CopilotMessage from "~/components/Copilot/conversation/CopilotMessage.vue";
import { CopilotRoleEnum } from "~/utils/enums/copilot/chat.enums";
import type { CopilotErrorRecoveryEnum } from "~/utils/enums/copilot/errors.enums";
import {
  canRateMessageAt,
  extrasAreStale,
  extrasHostIndexOf,
  isWaitingMessageAt,
  needsStandaloneExtras,
  needsStandaloneLoader,
  shouldRenderMessageAt,
} from "~/utils/helpers/copilot/conversation-layout";
import { isBareOf } from "~/utils/helpers/copilot/message-parts";
import type { ICopilotAction, ICopilotApproval, ICopilotUIMessage } from "~/utils/interfaces/copilot-interfaces";

/**
 * The streamed conversation: one bubble per message, the waiting sparkle, the thumbs, and a single
 * `extras` slot for whatever a caller has to attach to the last answer. Every placement rule lives
 * in `utils/helpers/copilot/conversation-layout` — this file binds them to the template.
 */
const props = withDefaults(
  defineProps<{
    messages: ICopilotUIMessage[];
    chatId: string;
    busy?: boolean;
    /** Retry budget of the answer at `index`; owned by `useCopilotChat`, not by the rendering. */
    canRetryAt?: (index: number) => boolean;
    /**
     * Whether the `extras` slot has something to show. The conversation needs to know before it
     * renders, because with no answer able to host them the extras get a bubble of their own.
     */
    hasExtras?: boolean;
    /**
     * The answer that produced what `extras` shows, when the slot's content belongs to one. Past it,
     * the extras stop painting rather than following the assistant's next reply.
     */
    extrasHostMessageId?: string | null;
  }>(),
  {
    busy: false,
    canRetryAt: () => false,
    hasExtras: false,
    extrasHostMessageId: null,
  },
);

const emit = defineEmits<{
  "pick-action": [action: ICopilotAction];
  approve: [payload: ICopilotApproval];
  deny: [payload: ICopilotApproval];
  "open-review": [review: AiCompanionTrainingReviewModel];
  "error-action": [recovery: CopilotErrorRecoveryEnum, messageId: string, index: number];
  retry: [messageId: string, index: number];
}>();

function shouldRender(index: number): boolean {
  return shouldRenderMessageAt(props.messages, index, props.busy);
}

function isWaiting(index: number): boolean {
  return isWaitingMessageAt(props.messages, index, props.busy);
}

function canRate(index: number): boolean {
  return canRateMessageAt(props.messages, index, props.busy);
}

const extrasHostIndex = computed(() =>
  props.hasExtras ? extrasHostIndexOf(props.messages, props.busy, props.extrasHostMessageId) : -1,
);

/** The admin has replied past the answer that produced them: history, not a card to stand alone. */
const areExtrasStale = computed(() => extrasAreStale(props.messages, props.extrasHostMessageId));

const hasStandaloneExtras = computed(() =>
  needsStandaloneExtras(props.hasExtras, extrasHostIndex.value, props.busy, areExtrasStale.value),
);

const hasStandaloneLoader = computed(() => needsStandaloneLoader(props.messages, props.busy));
</script>

<template>
  <template v-for="(message, index) of messages" :key="message.id">
    <CopilotMessage
      v-if="shouldRender(index)"
      :role="message.role === CopilotRoleEnum.USER ? CopilotRoleEnum.USER : CopilotRoleEnum.ASSISTANT"
      :bare="isBareOf(message)"
      :compact="isWaiting(index)"
    >
      <CopilotLoader v-if="isWaiting(index)" />
      <template v-else>
        <CopilotBlocks
          :message="message"
          :busy="busy"
          :can-retry="canRetryAt(index)"
          @pick-action="emit('pick-action', $event)"
          @approve="emit('approve', $event)"
          @deny="emit('deny', $event)"
          @open-review="emit('open-review', $event)"
          @error-action="emit('error-action', $event, message.id, index)"
          @retry="emit('retry', message.id, index)"
        />

        <!--
          Rendered here and nowhere else when an answer can host it: the slot appears exactly once
          in the conversation, so a caller cannot duplicate its card by mistake.
        -->
        <div v-if="index === extrasHostIndex" class="mt-3">
          <slot name="extras"></slot>
        </div>

        <CopilotFeedback
          v-if="canRate(index)"
          class="mt-3"
          :chat-id="chatId"
          :message-id="message.id"
          :response-id="message.metadata?.responseId"
        />
      </template>
    </CopilotMessage>
  </template>

  <CopilotMessage v-if="hasStandaloneExtras" :role="CopilotRoleEnum.ASSISTANT">
    <slot name="extras"></slot>
  </CopilotMessage>

  <CopilotMessage v-if="hasStandaloneLoader" :role="CopilotRoleEnum.ASSISTANT" compact>
    <CopilotLoader />
  </CopilotMessage>
</template>
